class SocialProofPopup extends HTMLElement {
  constructor() {
    super();
    this.oneShotTimers = new Set();
    this.driftTimer = null;
    this.hourCheckTimer = null;
    this.isDismissed = false;
  }

  connectedCallback() {
    try {
      if (sessionStorage.getItem('kimbo:social-proof:dismissed') === '1') {
        this.isDismissed = true;
        return;
      }
    } catch (e) {
      /* ignore */
    }

    const dataEl = this.querySelector('script[data-social-proof-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('Social proof: failed to parse data', e);
      return;
    }

    if (
      !this.data.productHandle ||
      !Array.isArray(this.data.hourlyCurve) ||
      this.data.hourlyCurve.length !== 24
    ) {
      return;
    }

    this.config = {
      minDelay: (parseInt(this.dataset.minDelay, 10) || 6) * 1000,
      driftMs: (parseInt(this.dataset.drift, 10) || 15) * 1000,
      minCountToShow: parseInt(this.data.minCountToShow, 10) || 2,
    };

    this.messageEl = this.querySelector('[data-popup-message]');
    this.closeBtn = this.querySelector('.social-proof-popup__close');

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dismissAll();
      });
    }

    this.onVisibility = () => {
      if (document.hidden) this.stopDrift();
      else this.startDrift();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.recomputeRange();
    this.current = this.initialCount();

    if (this.current < this.config.minCountToShow) return;

    this.scheduleOneShot(() => this.show(), this.config.minDelay);
  }

  disconnectedCallback() {
    this.oneShotTimers.forEach((id) => clearTimeout(id));
    this.oneShotTimers.clear();
    this.stopDrift();
    this.stopHourCheck();
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  scheduleOneShot(fn, ms) {
    const id = setTimeout(() => {
      this.oneShotTimers.delete(id);
      fn();
    }, ms);
    this.oneShotTimers.add(id);
    return id;
  }

  currentAuHour() {
    try {
      const hour = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        hour: 'numeric',
        hour12: false,
      }).format(new Date());
      const h = parseInt(hour, 10);
      return ((h % 24) + 24) % 24;
    } catch (e) {
      return new Date().getHours();
    }
  }

  recomputeRange() {
    const hour = this.currentAuHour();
    const mult = this.data.hourlyCurve[hour] || 0.5;
    const baseMin = Math.max(1, Math.round(this.data.baselineMin * mult));
    const baseMax = Math.max(baseMin + 1, Math.round(this.data.baselineMax * mult));
    this.hour = hour;
    this.min = baseMin;
    this.max = baseMax;
  }

  sessionSalt() {
    try {
      let s = sessionStorage.getItem('kimbo:social-proof:salt');
      if (!s) {
        s = String(Math.floor(Math.random() * 1e9));
        sessionStorage.setItem('kimbo:social-proof:salt', s);
      }
      return parseInt(s, 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  makeRng(seed) {
    let a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  hashStr(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return h >>> 0;
  }

  reseedRng() {
    const seed = this.hashStr(this.data.productHandle) ^ this.hour ^ this.sessionSalt();
    this.rng = this.makeRng(seed);
  }

  initialCount() {
    this.reseedRng();
    const range = this.max - this.min + 1;
    return this.min + Math.floor(this.rng() * range);
  }

  render() {
    if (!this.messageEl) return;
    const text = (this.data.templateText || '{count} people viewing this product right now')
      .replace('{count}', String(this.current));
    this.messageEl.textContent = text;
  }

  show() {
    if (this.isDismissed) return;
    this.render();
    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-active');
    });
    this.startDrift();
    this.startHourCheck();
  }

  startDrift() {
    if (this.driftTimer) return;
    if (document.hidden) return;
    if (!this.rng) return;
    this.driftTimer = setInterval(() => this.drift(), this.config.driftMs);
  }

  stopDrift() {
    if (this.driftTimer) {
      clearInterval(this.driftTimer);
      this.driftTimer = null;
    }
  }

  drift() {
    const r = this.rng();
    let d = 0;
    if (r < 0.25) d = -1;
    else if (r < 0.5) d = 1;
    const next = Math.min(this.max, Math.max(this.min, this.current + d));
    if (next !== this.current) {
      this.current = next;
      this.render();
    }
  }

  startHourCheck() {
    if (this.hourCheckTimer) return;
    this.hourCheckTimer = setInterval(() => {
      const h = this.currentAuHour();
      if (h !== this.hour) {
        this.recomputeRange();
        this.reseedRng();
        this.current = Math.min(this.max, Math.max(this.min, this.current));
        this.render();
      }
    }, 60_000);
  }

  stopHourCheck() {
    if (this.hourCheckTimer) {
      clearInterval(this.hourCheckTimer);
      this.hourCheckTimer = null;
    }
  }

  dismissAll() {
    this.isDismissed = true;
    this.stopDrift();
    this.stopHourCheck();
    this.oneShotTimers.forEach((id) => clearTimeout(id));
    this.oneShotTimers.clear();
    this.classList.remove('is-active');
    this.scheduleOneShot(() => {
      this.hidden = true;
    }, 400);
    try {
      sessionStorage.setItem('kimbo:social-proof:dismissed', '1');
    } catch (e) {
      /* ignore */
    }
  }
}

if (!customElements.get('social-proof-popup')) {
  customElements.define('social-proof-popup', SocialProofPopup);
}
